/**
 * Empty State Component
 *
 * Displayed when no task is selected. Provides entry points for creating rules.
 * Styled to match JotAudit design patterns.
 */

import React from 'react';
import {
  Add as AddIcon,
  Build as BuildIcon,
  Description as DocumentIcon,
  TextFields as PromptIcon,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface EmptyStateProps {
  onCreateTask: () => void;
  isCreating?: boolean;
}

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({
  icon,
  title,
  description,
  onClick,
  disabled = false,
  primary = false,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      onClick={disabled ? undefined : onClick}
      sx={{
        width: 200,
        p: 3,
        border: `1px solid ${primary ? theme.palette.primary.main : theme.palette.grey[200]}`,
        borderRadius: 3,
        bgcolor: primary ? `${theme.palette.primary.main}05` : theme.palette.background.paper,
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick && !disabled ? {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
          transform: 'translateY(-2px)',
        } : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        {/* Icon container */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: primary
              ? `${theme.palette.primary.main}15`
              : theme.palette.grey[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'textColors.darkText',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'textColors.lightText',
            lineHeight: 1.4,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Paper>
  );
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  onCreateTask,
  isCreating = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        maxWidth: 700,
        textAlign: 'center',
        p: 4,
      }}
    >
      {/* Header icon */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 3,
          bgcolor: `${theme.palette.primary.main}10`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BuildIcon
          sx={{
            fontSize: 36,
            color: theme.palette.primary.main,
          }}
        />
      </Box>

      {/* Title and description */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'textColors.darkText',
            mb: 1.5,
          }}
        >
          Rule Builder
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'textColors.lightText',
            maxWidth: 480,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Create compliance rules from documents, prompts, or start from scratch.
          Use AI chat to refine and perfect your rules.
        </Typography>
      </Box>

      {/* Action cards */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {/* Start Fresh - Primary action */}
        <ActionCard
          icon={
            isCreating ? (
              <CircularProgress size={28} />
            ) : (
              <AddIcon
                sx={{
                  fontSize: 28,
                  color: theme.palette.primary.main,
                }}
              />
            )
          }
          title="Start Fresh"
          description="Create rules from scratch with AI assistance"
          onClick={onCreateTask}
          disabled={isCreating}
          primary
        />

        {/* From Document */}
        <ActionCard
          icon={
            <DocumentIcon
              sx={{
                fontSize: 28,
                color: 'textColors.lightText',
              }}
            />
          }
          title="From Document"
          description="Extract rules from PDF documents"
        />

        {/* From Prompt */}
        <ActionCard
          icon={
            <PromptIcon
              sx={{
                fontSize: 28,
                color: 'textColors.lightText',
              }}
            />
          }
          title="From Prompt"
          description="Describe your rule in plain language"
        />
      </Box>

      {/* Help text */}
      <Typography
        variant="caption"
        sx={{
          color: 'textColors.lightText',
          mt: 2,
        }}
      >
        Select a task from the sidebar or create a new one to get started
      </Typography>
    </Box>
  );
};

export default EmptyState;
